import { create } from 'zustand';
import { supabase } from '../lib/supabaseClient';
import type { SimulationGraph } from '../models/simulation';
import type { Database } from '../lib/database.types';
import { errorService } from '../services/errorService';
import * as scenarioService from '../services/scenarioService';
import { normalizeUnit } from '../utils/units';

type DbSimulation = Database['public']['Tables']['simulations']['Row'];
type DbSimulationInsert = Database['public']['Tables']['simulations']['Insert'];
type DbSimulationUpdate = Database['public']['Tables']['simulations']['Update'];
type DbVersion = Database['public']['Tables']['versions']['Row'];
type DbVersionInsert = Database['public']['Tables']['versions']['Insert'];

/**
 * Normalizes all units in a graph by removing spaces around operators.
 * This ensures consistent unit formatting across the entire graph.
 */
function normalizeGraphUnits(graph: SimulationGraph): SimulationGraph {
    const normalizedGraph = structuredClone(graph);
    
    // Normalize units in distribution nodes
    normalizedGraph.nodes.forEach((node) => {
        if (node.data.type === 'distribution' && node.data.unit) {
            node.data.unit = normalizeUnit(node.data.unit);
        }
    });
    
    return normalizedGraph;
}

export interface Simulation {
    id: string;
    user_id: string;
    name: string;
    description?: string;
    status: 'active' | 'inactive';
    tags?: string[];
    is_example?: boolean;
    created_at: string;
    updated_at: string;
}

export interface SavedVersion {
    id: string;
    simulation_id: string;
    user_id: string;
    name: string;
    description?: string;
    graph: SimulationGraph;
    tags?: string[];
    is_autosave?: boolean;
    created_at: string;
}

interface VersionState {
    simulations: Simulation[];
    examples: Simulation[];
    versions: SavedVersion[];
    loading: boolean;
    error: string | null;

    // Simulation CRUD
    fetchSimulations: () => Promise<void>;
    fetchExamples: () => Promise<void>;
    createSimulation: (name: string, description?: string, tags?: string[]) => Promise<Simulation | null>;
    updateSimulation: (id: string, updates: Partial<Simulation>) => Promise<void>;
    deleteSimulation: (id: string) => Promise<void>;
    getSimulation: (id: string) => Simulation | undefined;
    isExample: (simulationId: string) => boolean;
    duplicateSimulation: (sourceSimulationId: string) => Promise<Simulation | null>;

    // Version CRUD
    fetchVersions: (simulationId?: string) => Promise<void>;
    saveVersion: (
        simulationId: string,
        graph: SimulationGraph,
        options?: {
            name?: string;
            description?: string;
            tags?: string[];
            autoSave?: boolean;
        }
    ) => Promise<SavedVersion | null>;
    deleteVersion: (versionId: string) => Promise<void>;
    getVersion: (versionId: string) => SavedVersion | undefined;
    getSimulationVersions: (simulationId: string) => SavedVersion[];
    getLatestVersion: (simulationId: string) => SavedVersion | undefined;

    // Access level
    getUserAccessLevel: (simulationId: string) => Promise<'owner' | 'editor' | 'viewer' | null>;

    // Local state helpers
    clearError: () => void;
}

const generateCopySimulationName = (baseName: string, simulations: Simulation[], examples: Simulation[]): string => {
    const existingNames = new Set<string>([...simulations, ...examples].map((sim) => sim.name));
    const copyPrefix = `Copy of ${baseName}`;
    if (!existingNames.has(copyPrefix)) {
        return copyPrefix;
    }

    let index = 2;
    let candidate = `${copyPrefix} (${index})`;
    while (existingNames.has(candidate)) {
        index += 1;
        candidate = `${copyPrefix} (${index})`;
    }

    return candidate;
};

export const useVersionStore = create<VersionState>((set, get) => ({
    simulations: [],
    examples: [],
    versions: [],
    loading: false,
    error: null,

    fetchSimulations: async () => {
        set({ loading: true, error: null });
        try {
            const { data, error } = await supabase
                .from('simulations')
                .select('*')
                .or('is_example.is.null,is_example.eq.false')
                .order('updated_at', { ascending: false });

            if (error) throw error;

            set({ simulations: data as Simulation[], loading: false });
        } catch (error: any) {
            errorService.logError('Error fetching simulations', error, {
                action: 'fetchSimulations'
            });
            set({ error: error.message, loading: false });
        }
    },

    fetchExamples: async () => {
        set({ loading: true, error: null });
        try {
            const { data, error } = await supabase
                .from('simulations')
                .select('*')
                .eq('is_example', true)
                .order('created_at', { ascending: true });

            if (error) throw error;

            set({ examples: data as Simulation[], loading: false });
        } catch (error: any) {
            errorService.logError('Error fetching examples', error, {
                action: 'fetchExamples'
            });
            set({ error: error.message, loading: false });
        }
    },

    createSimulation: async (name, description, tags) => {
        set({ loading: true, error: null });
        try {
            const {
                data: { user },
            } = await supabase.auth.getUser();
            if (!user) throw new Error('User not authenticated');

            const simulationData: DbSimulationInsert = {
                user_id: user.id,
                name,
                description: description || null,
                status: 'active',
                tags: tags || null,
            };

            const { data, error } = await supabase
                .from('simulations')
                .insert(simulationData as any)
                .select()
                .single();

            if (error) throw error;

            const newSimulation = data as Simulation;
            set((state) => ({
                simulations: [newSimulation, ...state.simulations],
                loading: false,
            }));

            return newSimulation;
        } catch (error: any) {
            errorService.logError('Error creating simulation', error, {
                action: 'createSimulation'
            });
            set({ error: error.message, loading: false });
            return null;
        }
    },

    updateSimulation: async (id, updates) => {
        set({ loading: true, error: null });
        try {
            const updateData: DbSimulationUpdate = {};
            if (updates.name !== undefined) updateData.name = updates.name;
            if (updates.description !== undefined) updateData.description = updates.description;
            if (updates.status !== undefined) updateData.status = updates.status;
            if (updates.tags !== undefined) updateData.tags = updates.tags;

            const { data, error } = await supabase
                .from('simulations')
                // @ts-ignore - Supabase type inference issue with update operations
                .update(updateData as any)
                .eq('id', id)
                .select()
                .single();

            if (error) throw error;
            if (!data) return;

            set((state) => ({
                simulations: state.simulations.map((s) => (s.id === id ? (data as unknown as Simulation) : s)),
                loading: false,
            }));
        } catch (error: any) {
            errorService.logError('Error updating simulation', error, {
                action: 'updateSimulation',
                metadata: { simulationId: id }
            });
            set({ error: error.message, loading: false });
        }
    },

    deleteSimulation: async (id) => {
        set({ loading: true, error: null });
        try {
            const { error } = await supabase.from('simulations').delete().eq('id', id);

            if (error) throw error;

            set((state) => ({
                simulations: state.simulations.filter((s) => s.id !== id),
                versions: state.versions.filter((v) => v.simulation_id !== id),
                loading: false,
            }));
        } catch (error: any) {
            errorService.logError('Error deleting simulation', error, {
                action: 'deleteSimulation',
                metadata: { simulationId: id }
            });
            set({ error: error.message, loading: false });
        }
    },

    getSimulation: (id) => {
        const simulation = get().simulations.find((s) => s.id === id);
        if (simulation) return simulation;
        // Also check examples
        return get().examples.find((s) => s.id === id);
    },

    isExample: (simulationId) => {
        const simulation = get().getSimulation(simulationId);
        return simulation?.is_example === true;
    },

    duplicateSimulation: async (sourceSimulationId) => {
        set({ loading: true, error: null });
        let createdSimulation: Simulation | null = null;

        try {
            const sourceSimulation = get().getSimulation(sourceSimulationId);
            if (!sourceSimulation) {
                throw new Error('Source simulation not found');
            }

            const latestVersion = get().getLatestVersion(sourceSimulationId);
            if (!latestVersion) {
                throw new Error('Source simulation has no versions to copy');
            }

            const {
                data: { user },
                error: userError,
            } = await supabase.auth.getUser();

            if (userError || !user) {
                throw new Error('User not authenticated');
            }

            const copyName = generateCopySimulationName(sourceSimulation.name, get().simulations, get().examples);

            const simulationInsert: DbSimulationInsert = {
                user_id: user.id,
                name: copyName,
                description: sourceSimulation.description || null,
                status: 'active',
                tags: sourceSimulation.tags || null,
            };

            const { data: insertedSimulation, error: insertError } = await supabase
                .from('simulations')
                .insert(simulationInsert as any)
                .select()
                .single();

            if (insertError || !insertedSimulation) {
                throw new Error(insertError?.message || 'Failed to create simulation copy');
            }

            createdSimulation = insertedSimulation as Simulation;

            set((state) => ({
                simulations: [createdSimulation!, ...state.simulations],
            }));

            const copiedVersion = await get().saveVersion(createdSimulation.id, latestVersion.graph, {
                name: latestVersion.name || `Version copied from ${sourceSimulation.name}`,
                description: latestVersion.description,
                tags: latestVersion.tags,
                autoSave: true,
            });

            if (!copiedVersion) {
                throw new Error('Failed to copy simulation version');
            }

            const graphTimestamp = latestVersion.graph?.dataUpdatedAt || new Date().toISOString();
            await scenarioService.duplicateScenarios(sourceSimulationId, createdSimulation.id, graphTimestamp);

            return createdSimulation;
        } catch (error: any) {
            if (createdSimulation) {
                try {
                    await supabase.from('simulations').delete().eq('id', createdSimulation.id);
                } catch {
                    // Ignore cleanup errors
                }

                set((state) => ({
                    simulations: state.simulations.filter((s) => s.id !== createdSimulation!.id),
                    versions: state.versions.filter((v) => v.simulation_id !== createdSimulation!.id),
                }));
            }

            const message = error instanceof Error ? error.message : 'Failed to copy simulation';
            errorService.logError('Error duplicating simulation', error, {
                action: 'duplicateSimulation',
                metadata: { sourceSimulationId },
            });
            set({ error: message });
            throw new Error(message);
        } finally {
            set((state) => ({ loading: false }));
        }
    },

    fetchVersions: async (simulationId) => {
        set({ loading: true, error: null });
        try {
            let query = supabase.from('versions').select('*');

            if (simulationId) {
                query = query.eq('simulation_id', simulationId);
            }

            const { data, error } = await query.order('created_at', { ascending: false });

            if (error) throw error;

            const versions = (data as DbVersion[]).map((v: DbVersion) => {
                const graph = (v.graph_data as unknown) as SimulationGraph;
                // Normalize units when loading from database
                const normalizedGraph = normalizeGraphUnits(graph);
                return {
                    ...v,
                    graph: normalizedGraph,
                    description: v.description || undefined,
                    tags: v.tags || undefined,
                    is_autosave: v.is_autosave || undefined,
                };
            });

            set({ versions, loading: false });
        } catch (error: any) {
            errorService.logError('Error fetching versions', error, {
                action: 'fetchVersions',
                metadata: { simulationId }
            });
            set({ error: error.message, loading: false });
        }
    },

    saveVersion: async (simulationId, graph, options = {}) => {
        const { name, description, tags, autoSave } = options;

        // Check if user is viewer - viewers cannot save
        const accessLevel = await get().getUserAccessLevel(simulationId);
        if (accessLevel === 'viewer') {
            throw new Error('Viewers cannot save changes to simulations');
        }

        if (autoSave) {
            set({ error: null });
        } else {
            set({ loading: true, error: null });
        }
        try {
            const {
                data: { user },
            } = await supabase.auth.getUser();
            if (!user) throw new Error('User not authenticated');

            const generateAutoSaveName = () => {
                const formatter = new Intl.DateTimeFormat('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                });
                return `Auto-saved ${formatter.format(new Date())}`;
            };

            let versionName = name?.trim();
            if (autoSave) {
                versionName = versionName || generateAutoSaveName();
            }

            if (!versionName) {
                throw new Error('Version name is required');
            }

            // Normalize units in the graph before saving
            const normalizedGraph = normalizeGraphUnits(graph);
            
            const versionData: DbVersionInsert = {
                simulation_id: simulationId,
                user_id: user.id,
                name: versionName,
                description: description?.trim() || null,
                graph_data: normalizedGraph as any,
                tags: tags && tags.length > 0 ? tags : null,
                is_autosave: autoSave || false,
            };

            const { data, error } = await supabase
                .from('versions')
                .insert(versionData as any)
                .select()
                .single();

            if (error) throw error;

            const dbVersion = data as DbVersion;
            const newVersion: SavedVersion = {
                id: dbVersion.id,
                simulation_id: dbVersion.simulation_id,
                user_id: dbVersion.user_id,
                name: dbVersion.name,
                description: dbVersion.description || undefined,
                graph: (dbVersion.graph_data as unknown) as SimulationGraph,
                tags: dbVersion.tags || undefined,
                is_autosave: dbVersion.is_autosave || undefined,
                created_at: dbVersion.created_at,
            };

            set((state) => ({
                versions: [newVersion, ...state.versions],
                loading: autoSave ? state.loading : false,
            }));

            // Update simulation's updated_at timestamp (fire-and-forget)
            void supabase
                .from('simulations')
                // @ts-ignore - Supabase type inference issue with update operations
                .update({ updated_at: new Date().toISOString() } as any)
                .eq('id', simulationId)
                .then(
                    () => undefined,
                    () => undefined
                );

            // Refresh simulations to get updated timestamp when manual save
            if (!autoSave) {
                get().fetchSimulations();
            }

            return newVersion;
        } catch (error: any) {
            errorService.logError('Error saving version', error, {
                action: 'saveVersion',
                metadata: { simulationId, autoSave }
            });
            set({ error: error.message, loading: false });
            return null;
        }
    },

    deleteVersion: async (versionId) => {
        set({ loading: true, error: null });
        try {
            const { error } = await supabase.from('versions').delete().eq('id', versionId);

            if (error) throw error;

            set((state) => ({
                versions: state.versions.filter((v) => v.id !== versionId),
                loading: false,
            }));
        } catch (error: any) {
            errorService.logError('Error deleting version', error, {
                action: 'deleteVersion',
                metadata: { versionId }
            });
            set({ error: error.message, loading: false });
        }
    },

    getVersion: (versionId) => {
        return get().versions.find((v) => v.id === versionId);
    },

    getSimulationVersions: (simulationId) => {
        return get().versions.filter((v) => v.simulation_id === simulationId)
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    },

    getLatestVersion: (simulationId) => {
        const versions = get().getSimulationVersions(simulationId);
        return versions[0];
    },

    getUserAccessLevel: async (simulationId) => {
        try {
            const {
                data: { user },
            } = await supabase.auth.getUser();
            if (!user) return null;

            // Check if user is the owner
            const simulation = get().getSimulation(simulationId);
            if (simulation && simulation.user_id === user.id) {
                return 'owner';
            }

            // Check simulation_access table
            const { data, error } = await supabase
                .from('simulation_access')
                .select('access_level')
                .eq('simulation_id', simulationId)
                .eq('user_id', user.id)
                .single<{ access_level: 'owner' | 'editor' | 'viewer' }>();

            if (error || !data) {
                // If no access record found, user is not owner/editor/viewer
                // For shared simulations, default to viewer if they can see it
                // But if they can't see it, return null
                return null;
            }

            return data.access_level || null;
        } catch (error: any) {
            errorService.logError('Error fetching access level', error, {
                action: 'getUserAccessLevel',
                metadata: { simulationId }
            });
            return null;
        }
    },

    clearError: () => set({ error: null }),
}));
